import { KazokugotoService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { IKazokugotoTopic, KazokugotoTheme } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const THEME_LABEL: Record<KazokugotoTheme, string> = {
  asset: '資産',
  health: '健康',
  safety: '安全',
  family: '家族',
};

const THEME_COLOR: Record<KazokugotoTheme, string> = {
  asset: 'gold',
  health: 'green',
  safety: 'blue',
  family: 'magenta',
};

// あゆみ。の課題（短縮ID → 表示名）。サーバーの AYUMI_TOPIC_LABELS と揃える
const AYUMI_LABEL: Record<string, string> = {
  asset: '資産管理',
  work: '仕事と介護',
  life: '暮らしの変化',
  medical: '受診・医療',
  drive: '移動と運転',
  lonely: '孤立を防ぐ',
  stigma: '正しい理解',
};

const AYUMI_OPTIONS = Object.entries(AYUMI_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}月`,
}));

// 365日マスタの年版。今年を中心に前後1年を選択できる
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(
  (y) => ({ value: y, label: `${y}年` }),
);

interface IProps {
  title?: string;
}

export const KazokugotoTopicList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [month, setMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  const [editTarget, setEditTarget] = useState<IKazokugotoTopic | null>(null);
  const [editForm, setEditForm] = useState<{
    theme: KazokugotoTheme;
    title: string;
    sub: string;
    ayumiId: string | null;
  }>({ theme: 'asset', title: '', sub: '', ayumiId: null });

  const { data, isFetching } = useQuery(
    [ENDPOINT.KAZOKUGOTO.LIST_TOPICS, year, month],
    () => KazokugotoService.listTopics(year, month),
  );

  const updateMutation = useMutation(
    (params: {
      id: number;
      body: {
        theme: KazokugotoTheme;
        title: string;
        sub: string;
        ayumiId: string | null;
      };
    }) => KazokugotoService.updateTopic(params.id, params.body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'トピックを更新しました',
          type: NOTIFY_STATUS.SUCCESS,
        });
        setEditTarget(null);
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_TOPICS]);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `更新に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const handleStartEdit = (topic: IKazokugotoTopic) => {
    setEditTarget(topic);
    setEditForm({
      theme: topic.theme,
      title: topic.title,
      sub: topic.sub,
      ayumiId: topic.ayumiId ?? null,
    });
  };

  const handleSaveEdit = () => {
    if (!editTarget) return;
    if (!editForm.title.trim()) {
      notificationMessage({
        message: 'タイトルは必須です',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    updateMutation.mutate({ id: editTarget.id, body: editForm });
  };

  const columns: TableColumnsType<IKazokugotoTopic> = [
    {
      title: '日',
      dataIndex: 'day',
      width: 70,
      render: (d: number) => <Text strong>{d}日</Text>,
    },
    {
      title: 'テーマ',
      dataIndex: 'theme',
      width: 90,
      render: (t: KazokugotoTheme) => (
        <Tag color={THEME_COLOR[t]}>{THEME_LABEL[t]}</Tag>
      ),
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      width: 280,
    },
    {
      title: 'サブ',
      dataIndex: 'sub',
      ellipsis: true,
    },
    {
      title: 'あゆみ。',
      dataIndex: 'ayumiId',
      width: 120,
      render: (id: string | null | undefined) =>
        id ? (
          <Tag color='purple'>{AYUMI_LABEL[id] ?? id}</Tag>
        ) : (
          <Text type='secondary'>なし</Text>
        ),
    },
    {
      title: '操作',
      width: 90,
      render: (_, record) => (
        <Button size='small' onClick={() => handleStartEdit(record)}>
          編集
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            かぞくごと。365日マスタ
          </Title>
          <Paragraph type='secondary'>
            記事・漫才生成の元になるトピックです。テーマ・タイトル・サブ・あゆみ。の課題を編集できます。編集後にその日付を再生成すると新しい内容が反映されます。
          </Paragraph>
        </header>

        <Card
          size='small'
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          styles={{ header: { background: '#fff' }, body: { background: '#fff' } }}
        >
          <Form layout='inline'>
            <Form.Item label='年'>
              <Select
                style={{ width: 120 }}
                value={year}
                onChange={(v) => setYear(v)}
                options={YEAR_OPTIONS}
              />
            </Form.Item>
            <Form.Item label='月'>
              <Select
                style={{ width: 120 }}
                value={month}
                onChange={(v) => setMonth(v)}
                options={MONTH_OPTIONS}
              />
            </Form.Item>
          </Form>
        </Card>

        <Card
          size='small'
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          styles={{ header: { background: '#fff' }, body: { background: '#fff' } }}
        >
          <Table<IKazokugotoTopic>
            rowKey='id'
            loading={isFetching}
            columns={columns}
            dataSource={data?.data ?? []}
            pagination={false}
            scroll={{ x: 700 }}
            locale={{ emptyText: 'トピックがありません' }}
          />
        </Card>
      </div>

      <Modal
        title={
          editTarget
            ? `トピック編集: ${editTarget.year}年${editTarget.month}月${editTarget.day}日`
            : ''
        }
        open={!!editTarget}
        onCancel={() => setEditTarget(null)}
        onOk={handleSaveEdit}
        confirmLoading={updateMutation.isLoading}
        okText='保存'
        cancelText='キャンセル'
        width={640}
      >
        <Form layout='vertical'>
          <Form.Item label='テーマ' required>
            <Select
              value={editForm.theme}
              onChange={(v) =>
                setEditForm((f) => ({ ...f, theme: v as KazokugotoTheme }))
              }
              options={[
                { value: 'asset', label: '資産' },
                { value: 'health', label: '健康' },
                { value: 'safety', label: '安全' },
                { value: 'family', label: '家族' },
              ]}
            />
          </Form.Item>
          <Form.Item label='タイトル' required>
            <Input
              value={editForm.title}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, title: e.target.value }))
              }
              maxLength={200}
              showCount
            />
          </Form.Item>
          <Form.Item label='サブ'>
            <TextArea
              value={editForm.sub}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, sub: e.target.value }))
              }
              autoSize={{ minRows: 2, maxRows: 5 }}
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item
            label='あゆみ。の課題'
            extra='記事末に「あゆみ。で確認する」導線を出す課題です。未選択なら導線は出ません。'
          >
            <Select
              allowClear
              placeholder='なし（導線を出さない）'
              value={editForm.ayumiId ?? undefined}
              onChange={(v) =>
                setEditForm((f) => ({ ...f, ayumiId: v ?? null }))
              }
              options={AYUMI_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
